import { useLazyQuery } from '@apollo/client';
import { instanceToPlain } from 'class-transformer';
import React, { useContext, useMemo } from 'react';
import { CompanyRoleAssignmentContext } from '../context/CompanyRoleAssignmentContext';
import {
  StockLocationRoleAssignmentContext,
  StockLocationRoleAssignmentContextProps,
  stockLocationRoleAssignmentSlice,
} from '../context/StockLocationRoleAssignmentContext';
import { SuperUserContext } from '../context/SuperUserContext';
import { UserContext } from '../context/UserContext';
import {
  GetStockLocationRoleAssignmentsResponse,
  GetStockLocationRoleAssignmentsVariables,
  StockLocationRoleAssignmentQueries,
} from '../graphql/stockLocationRoleAssignment.graphql';
import { CompanyRole } from '../types/company_role_assignment/companyRoleAssignment';
import {
  StockLocationRole,
  StockLocationRoleAssignment,
} from '../types/stock_location_role_assignment/stockLocationRoleAssignment';
import { SuperUserRole } from '../types/superUser';
import { useAppDispatch, useAppSelector } from '../ui/pages/store';

interface StockLocationRoleAssignmentProviderProps {
  children: React.ReactNode;
}

export default function StockLocationRoleAssignmentProvider({ children }: StockLocationRoleAssignmentProviderProps) {
  const dispatch = useAppDispatch();
  const items = useAppSelector(state => state.stockLocationRoleAssignments.items);
  const loading = useAppSelector(state => state.stockLocationRoleAssignments.loading);
  const { currentUser } = useContext(UserContext);
  const { superUser } = useContext(SuperUserContext);
  const { hasCompanyRole } = useContext(CompanyRoleAssignmentContext);

  const stockLocationRoleAssignments = useMemo(() => {
    const map = new Map<string, StockLocationRoleAssignment[]>();
    for (const item of items) {
      const stockLocationRoleAssignment = new StockLocationRoleAssignment(item);
      const arr = map.get(stockLocationRoleAssignment.stockLocationId) || [];
      map.set(item.stockLocationId, [...arr, stockLocationRoleAssignment]);
    }
    return map;
  }, [items]);

  const [refreshStockLocationRolesQuery] = useLazyQuery<
    GetStockLocationRoleAssignmentsResponse,
    GetStockLocationRoleAssignmentsVariables
  >(StockLocationRoleAssignmentQueries.get, {
    onCompleted: async res => {
      dispatch(stockLocationRoleAssignmentSlice.actions.addAll(res.stockLocationRolesForCompany.data));
    },
  });

  const currentStockLocationRole = (stockLocationId: string) => {
    return stockLocationRoleAssignments.get(stockLocationId)?.find(slr => slr.userId === currentUser?.id)?.role;
  };

  const hasStockLocationRoleInCompany = (companyId: string, role: StockLocationRole, stockLocationId?: string) => {
    if (superUser?.role === SuperUserRole.administrator) return true;
    if (hasCompanyRole(companyId, CompanyRole.administrator)) return true;

    const stockLocationIds = stockLocationId ? [stockLocationId] : [...stockLocationRoleAssignments.keys()];
    return stockLocationIds.some(id => hasStockLocationRole(companyId, id, role));
  };

  const hasStockLocationRole = (companyId: string, stockLocationId: string, role: StockLocationRole) => {
    if (superUser?.role === SuperUserRole.administrator) return true;
    if (hasCompanyRole(companyId, CompanyRole.administrator)) return true;

    const userRole = currentStockLocationRole(stockLocationId);
    switch (role) {
      case StockLocationRole.STOCK_LOCATION_VIEWER:
        if (userRole === StockLocationRole.STOCK_LOCATION_VIEWER) return true;
      case StockLocationRole.STOCK_LOCATION_USER:
        if (userRole === StockLocationRole.STOCK_LOCATION_USER) return true;
      case StockLocationRole.STOCK_LOCATION_SUPERVISOR:
        if (userRole === StockLocationRole.STOCK_LOCATION_SUPERVISOR) return true;
      case StockLocationRole.STOCK_LOCATION_MANAGER:
        if (userRole === StockLocationRole.STOCK_LOCATION_MANAGER) return true;
      default:
        return false;
    }
  };

  const stockLocationRoleAssignmentContext: StockLocationRoleAssignmentContextProps = {
    stockLocationRoles: stockLocationRoleAssignments,
    stockLocationRolesLoading: loading,
    setStockLocationRoles: (roles: Map<string, StockLocationRoleAssignment[]>) => {
      dispatch(stockLocationRoleAssignmentSlice.actions.setAll([...roles.values()].flatMap(r => instanceToPlain(r))));
    },
    refreshStockLocationRoles: async (companyId: string) => {
      dispatch(stockLocationRoleAssignmentSlice.actions.reset());
      await refreshStockLocationRolesQuery({ variables: { companyId } });
    },
    currentStockLocationRole: (stockLocationId: string) => {
      return stockLocationRoleAssignments.get(stockLocationId)?.find(slr => slr.userId === currentUser?.id)?.role;
    },
    hasStockLocationRole: hasStockLocationRole,
    hasStockLocationRoleInCompany: hasStockLocationRoleInCompany,
  };

  return (
    <StockLocationRoleAssignmentContext.Provider value={stockLocationRoleAssignmentContext}>
      {children}
    </StockLocationRoleAssignmentContext.Provider>
  );
}
